import { useTranslation } from "react-i18next";
import { formatDateTime, formatPerson, formatTime } from "@/utils/helper";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { SectionTitle } from "@/components/ui/SectionTitle";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import {
  CalendarArrowUp,
  CalendarRange,
  CalendarX,
  ChevronLeft,
  Coffee,
  FlagTriangleRight,
  Info,
  Languages,
  MapPin,
  Paperclip,
  Theater,
  User,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { cn } from "@/utils/cn";
import { startCase } from "lodash";
import { Spinner } from "@/components/ui/v2/spinner";
import { MS_DYNAMIC_EVENT_ID_KEY, MS_EVENT_PATHS } from "@/routes/my-space/routes.events";
import { EventsCalendar } from "./events-calendar";
import {
  useMsAcceptEventInvitationMutation,
  useMsGetEventByIdQuery,
  useMsRejectEventInvitationMutation,
} from "@/services/(my-space)/api.events";
import toast from "react-hot-toast";
import { EVENT_INVITATION_STATUSES, EVENT_TYPES } from "@/types/(company)/events.interface";
import { EmptyState } from "@/components/EmptyState";

export const EventInfo = () => {
  const { t } = useTranslation("my-space");

  const navigate = useNavigate();
  const { eventId } = useParams<{ [MS_DYNAMIC_EVENT_ID_KEY]: string }>();
  const { data: invitation, isFetching } = useMsGetEventByIdQuery({ eventId: eventId! }, { skip: !eventId });
  const event = invitation?.data.event;

  const eventData: {
    label: string;
    value: string | ReactNode;
    icon: LucideIcon;
    iconClasses: string;
  }[] = [
    {
      label: t("ui.event_name"),
      value: event?.title,
      icon: Theater,
      iconClasses: "bg-info-muted text-info",
    },
    {
      label: t("ui.event_type"),
      value: startCase(event?.type),
      icon: CalendarRange,
      iconClasses: "bg-orange-100 text-orange-500",
    },
    {
      label: t("labels.start_date"),
      value: event?.startDateTime ? formatDateTime(event?.startDateTime) : null,
      icon: CalendarRange,
      iconClasses: "bg-green-100 text-green-500",
    },
    {
      label: t("labels.end_date"),
      value: event?.startDateTime ? formatDateTime(event?.endDateTime) : null,

      icon: CalendarRange,
      iconClasses: "bg-green-100 text-green-500",
    },
    {
      label: t("ui.location"),
      value:
        event?.type === EVENT_TYPES.HYBRID ? (
          <div className="flex flex-col gap-1">
            <span>{event?.location ?? t("events.unavailable")}</span>
            {event?.meetingLink ? (
              <a
                href={event.meetingLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline break-all normal-case!"
              >
                {event.meetingLink}
              </a>
            ) : (
              <span className="text-black/40">{t("events.no_meeting_link")}</span>
            )}
          </div>
        ) : event?.type === EVENT_TYPES.VIRTUAL ? (
          event?.meetingLink ? (
            <a
              href={event.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline break-all normal-case!"
            >
              {event.meetingLink}
            </a>
          ) : (
            "Unavailable"
          )
        ) : (
          (event?.location ?? t("events.unavailable"))
        ),
      icon: MapPin,
      iconClasses: "bg-green-100 text-green-500",
    },
    {
      label: t("ui.description"),
      value: event?.description,
      icon: Info,
      iconClasses: "bg-amber-100 text-amber-500",
    },
    {
      label: t("ui.repeat_event"),
      value: startCase(event?.recurrence),
      icon: CalendarArrowUp,
      iconClasses: "bg-info-muted text-info",
    },
    ...(event?.language
      ? [
          {
            label: t("labels.language"),
            value: event.language,
            icon: Languages,
            iconClasses: "bg-info-muted text-info",
          },
        ]
      : []),
    {
      label: t("ui.attachments"),
      value: (
        <ul className="grid grid-flow-row gap-0.5">
          {!event?.attachments?.length
            ? t("ui.no_attachments")
            : event?.attachments.map((file) => (
                <li key={file.id}>
                  <Link to={file.fileUrl} target="_blank" className="text-primary underline">
                    {file.fileName}
                  </Link>
                </li>
              ))}
        </ul>
      ),
      icon: Paperclip,
      iconClasses: "bg-red-100 text-red-500",
    },
    {
      label: t("ui.created_by"),
      value: formatPerson(event?.createdBy),
      icon: User,
      iconClasses: "bg-pink-100 text-pink-500",
    },
    {
      label: t("ui.approval_status"),
      value: invitation?.data?.invitationStatus,
      icon: FlagTriangleRight,
      iconClasses: "bg-violet-100 text-violet-500",
    },
  ];

  const [acceptInvitation, { isLoading: isAccepting }] = useMsAcceptEventInvitationMutation();
  const [rejectInvitation, { isLoading: isRejecting }] = useMsRejectEventInvitationMutation();
  const { can } = usePermissions();
  const canEdit = can(PLATFORM_SECTION_ENUM.EVENTS, "edit");

  const handleAccept = async (invitationId: string) => {
    const id = toast.loading("Processing...");
    await acceptInvitation({ invitationId })
      .unwrap()
      .then((res) => toast.success(res?.message, { id }))
      .catch((e) => {
        toast.error(e?.message ?? e?.data?.message ?? e?.response?.data?.message, { id });
        console.error(e);
      });
  };

  const handleReject = async (invitationId: string) => {
    const id = toast.loading("Processing...");
    await rejectInvitation({ invitationId })
      .unwrap()
      .then((res) => toast.success(res?.message, { id }))
      .catch((e) => {
        toast.error(e?.message ?? e?.data?.message ?? e?.response?.data?.message, { id });
        console.error(e);
      });
  };

  return (
    <Card className="h-full flex-1 flex flex-col">
      {isFetching ? (
        <Spinner />
      ) : !event ? (
        <EmptyState
          icon={CalendarX}
          title={t("events.invitation_not_found")}
          description={t("ui.the_invitation_you_are_looking_for_does_not_exist_or_has_been_de")}
        />
      ) : (
        <>
          <CardHeader className="flex flex-row items-center gap-2 px-2 md:p-4 shrink-0">
            <Button size="icon" variant="outline" aria-label={t("events.back_to_events")} data-tooltip-id="hrm-tooltip" data-tooltip-content={t("ui.back_to_events")} className="w-max" onClick={() => navigate(MS_EVENT_PATHS.ROOT)}>
              <ChevronLeft className="text-primary rtl-flip" />
            </Button>
            <div className="text-sm">
              <SectionTitle title={event?.title ?? ""} noMarginBottom />
              <p className="text-sm -mt-1 text-black/50 max-w-sm truncate">{event?.description}</p>
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 flex flex-col min-h-0">
            <div className="flex flex-wrap gap-8 flex-1">
              <div className="flex-1 flex flex-col px-4 w-1/2">
                {event?.posterUrl && (
                  <img
                    src={event.posterUrl}
                    alt={t("ui.event_poster")}
                    className="mb-6 max-h-56 w-full rounded-sxs border object-cover"
                  />
                )}
                <SectionTitle title={t("events.details")} size="sm" />
                <div className="grid min-[1174px]:grid-cols-2 min-[1370px]:grid-cols-3 gap-x-4 gap-y-8">
                  {eventData.map(({ label, value, icon: Icon, iconClasses }, i) => (
                    <div key={i} className="flex gap-4">
                      <div className={cn("p-2 size-10 rounded-sxs bg-info-muted grid place-items-center", iconClasses)}>
                        <Icon />
                      </div>
                      <div className="text-sm">
                        <p className="uppercase text-black/50">{label}</p>
                        <span className="font-medium capitalize">{value}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {event?.breaks && event.breaks.length > 0 && (
                  <div className="mt-8">
                    <SectionTitle title={t("events.breaks")} size="sm" />
                    <ul className="mt-2 space-y-2">
                      {event.breaks.map((brk, i) => (
                        <li
                          key={brk.id ?? i}
                          className="flex items-center gap-3 rounded-sxs border p-2 text-sm"
                        >
                          <div className="grid size-9 place-items-center rounded-sxs bg-info-muted text-info">
                            <Coffee className="size-4" />
                          </div>
                          <div className="flex flex-1 items-center justify-between">
                            <span className="font-medium">{brk.title || t("ui.break")}</span>
                            <span className="text-black/60">
                              {formatTime(brk.startDateTime)} – {formatTime(brk.endDateTime)}
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="w-full md:w-[450px] px-4 mt-6 md:mt-0">
                <EventsCalendar />
              </div>
            </div>
            {invitation && invitation?.data?.id && (
              <div className="mt-6 border-t border-black/10 flex flex-wrap justify-center md:justify-end gap-2 p-4 shrink-0">
                <Button
                  variant="outline"
                  disabled={isRejecting}
                  onClick={() => navigate(MS_EVENT_PATHS.ROOT)}
                  className="flex items-center gap-2 md:max-w-32"
                >
                  {t("ui.cancel")}
                </Button>
                <Button
                  variant="danger"
                  disabled={isRejecting || !canEdit || invitation?.data.invitationStatus !== EVENT_INVITATION_STATUSES.PENDING}
                  onClick={() => handleReject(String(invitation.data.event.id))}
                  className="flex items-center gap-2 md:max-w-32"
                >
                  {t("ui.reject")}
                </Button>
                <Button
                  variant="outline-success"
                  disabled={isAccepting || !canEdit || invitation?.data.invitationStatus !== EVENT_INVITATION_STATUSES.PENDING}
                  onClick={() => handleAccept(String(invitation.data.event.id))}
                  className="flex items-center gap-2 md:max-w-32"
                >
                  {t("ui.accept")}
                </Button>
              </div>
            )}
          </CardContent>
        </>
      )}
    </Card>
  );
};
